/**
 * Logger Service
 * Structured logging with multiple transports
 * 
 * @module lib/logger
 */

import {
    LogLevel,
    LogEntry,
    LogContext,
    LoggerConfig,
    ILogger,
    LogTransport,
    LOG_LEVEL_PRIORITY
} from './types';
import { ConsoleTransport, RemoteTransport, SentryTransport } from './transports';

// =============================================================================
// PII Scrubbing
// =============================================================================

const DEFAULT_PII_FIELDS = [
    'password',
    'token',
    'accessToken',
    'refreshToken',
    'apiKey',
    'secret',
    'creditCard',
    'ssn',
    'email',
    'phone',
];

function scrubPII(
    data: Record<string, unknown>,
    piiFields: string[],
    seen = new WeakSet<object>(),
    depth = 0
): Record<string, unknown> {
    if (depth > 10) return { _scrubbed: '[MAX_DEPTH_EXCEEDED]' };
    if (seen.has(data)) return { _scrubbed: '[CIRCULAR]' };

    seen.add(data);
    const scrubbed: Record<string, unknown> = {};

    try {
        for (const [key, value] of Object.entries(data)) {
            const lowerKey = key.toLowerCase();
            const isPII = piiFields.some(field => lowerKey.includes(field.toLowerCase()));

            if (isPII) {
                scrubbed[key] = '[REDACTED]';
            } else if (typeof value === 'object' && value !== null) {
                if (seen.has(value as object)) {
                    scrubbed[key] = '[CIRCULAR]';
                } else if (!Array.isArray(value)) {
                    scrubbed[key] = scrubPII(value as Record<string, unknown>, piiFields, seen, depth + 1);
                } else {
                    // Handle arrays by scrubbing their items but preventing circular refs inside
                    scrubbed[key] = (value as unknown[]).map(item => {
                        if (typeof item === 'object' && item !== null) {
                            if (seen.has(item as object)) return '[CIRCULAR]';
                            return !Array.isArray(item)
                                ? scrubPII(item as Record<string, unknown>, piiFields, seen, depth + 1)
                                : item; // Shallow copy arrays for now or recurse if needed, but simplistic approach
                        }
                        return item;
                    });
                }
            } else {
                scrubbed[key] = value;
            }
        }
    } catch (e) {
        return { _error: 'Failed to scrub PII' };
    }

    return scrubbed;
}

// =============================================================================
// Logger Class
// =============================================================================

class Logger implements ILogger {
    private config: LoggerConfig;
    private context: LogContext;

    constructor(config: Partial<LoggerConfig> = {}, context: LogContext = {}) {
        // Default configuration based on environment
        const isDev = typeof process !== 'undefined'
            ? process.env.NODE_ENV === 'development'
            : true;

        this.config = {
            minLevel: isDev ? 'debug' : 'info',
            transports: config.transports ?? [
                new ConsoleTransport({ minLevel: isDev ? 'debug' : 'info' }),
                // Add RemoteTransport and SentryTransport in production
                ...(isDev ? [] : [
                    new RemoteTransport({ minLevel: 'error' }),
                    new SentryTransport({ minLevel: 'error' })
                ]),
            ],
            defaultContext: config.defaultContext ?? {},
            includeStackTrace: config.includeStackTrace ?? isDev,
            piiFields: config.piiFields ?? DEFAULT_PII_FIELDS,
            enabled: config.enabled ?? true,
            ...config,
        };

        this.context = { ...this.config.defaultContext, ...context };
    }

    private shouldLog(level: LogLevel): boolean {
        if (!this.config.enabled) return false;
        return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.config.minLevel];
    }

    private createEntry(
        level: LogLevel,
        message: string,
        error?: Error | unknown,
        data?: Record<string, unknown>
    ): LogEntry {
        const entry: LogEntry = {
            level,
            message,
            timestamp: new Date().toISOString(),
            context: { ...this.context },
            environment: typeof process !== 'undefined'
                ? (process.env.NODE_ENV ?? 'development')
                : 'browser',
        };

        // Add data (with PII scrubbing)
        if (data) {
            entry.data = scrubPII(data, this.config.piiFields ?? []);
        }

        // Add error information
        if (error) {
            if (error instanceof Error) {
                entry.error = {
                    name: error.name,
                    message: error.message,
                    stack: this.config.includeStackTrace ? error.stack : undefined,
                    code: (error as { code?: string }).code,
                };
            } else {
                entry.error = {
                    name: 'UnknownError',
                    message: String(error),
                };
            }
        }

        return entry;
    }

    private log(
        level: LogLevel,
        message: string,
        error?: Error | unknown,
        data?: Record<string, unknown>
    ): void {
        if (!this.shouldLog(level)) return;

        const entry = this.createEntry(level, message, error, data);

        // Send to all transports
        for (const transport of this.config.transports) {
            try {
                transport.log(entry);
            } catch (err) {
                console.error(`[Logger] Transport ${transport.name} failed:`, err);
            }
        }
    }

    // Public logging methods
    debug(message: string, data?: Record<string, unknown>): void {
        this.log('debug', message, undefined, data);
    }

    info(message: string, data?: Record<string, unknown>): void {
        this.log('info', message, undefined, data);
    }

    warn(message: string, data?: Record<string, unknown>): void {
        this.log('warn', message, undefined, data);
    }

    error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
        this.log('error', message, error, data);
    }

    fatal(message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
        this.log('fatal', message, error, data);
    }

    // Create child logger with additional context
    child(context: LogContext): ILogger {
        return new Logger(this.config, { ...this.context, ...context });
    }

    // Set context for this logger
    setContext(context: LogContext): void {
        this.context = { ...this.context, ...context };
    }

    // Flush all transports
    async flush(): Promise<void> {
        await Promise.all(
            this.config.transports
                .filter(t => t.flush)
                .map(t => t.flush!())
        );
    }

    // Add a transport
    addTransport(transport: LogTransport): void {
        this.config.transports.push(transport);
    }

    // Configure the logger
    configure(config: Partial<LoggerConfig>): void {
        this.config = { ...this.config, ...config };
    }
}

// =============================================================================
// Singleton Export
// =============================================================================

/** Global logger instance */
export const logger = new Logger();

/** Create a new logger with custom config */
export function createLogger(config?: Partial<LoggerConfig>, context?: LogContext): ILogger {
    return new Logger(config, context);
}

// Re-export types
export * from './types';
export { ConsoleTransport, RemoteTransport, SentryTransport } from './transports';
